import { AlertCircle, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import type { HealthState } from '@/lib/api';

/* SPEC-448 — one place that says what each health state looks like and reads as.
 *
 * Both surfaces that render a state (the pipeline map and the health grid) key off this
 * table, so a colour can never mean one thing in the diagram and another in the grid.
 *
 * The honesty contract is in the palette itself: `unknown` is GREY and named. It is never
 * green and never red. A stage the platform does not measure saying "reporting normally"
 * is the one failure this screen cannot have.
 */

export const STATE_RANK: Record<HealthState, number> = {
  unknown: 0,
  healthy: 1,
  caution: 2,
  degraded: 3,
};

export const STATE_ORDER: HealthState[] = ['degraded', 'caution', 'unknown', 'healthy'];

interface StateMeta {
  /** Short label for a badge or a column header. */
  label: string;
  /** Tailwind text colour. */
  text: string;
  /** Ring colour for the node card. */
  ring: string;
  /** Dot/pill background. */
  dot: string;
  /** Soft background for a pill. */
  pill: string;
}

export const STATE_META: Record<HealthState, StateMeta> = {
  degraded: {
    label: 'Needs attention',
    text: 'text-red-400',
    ring: 'ring-red-500/70',
    dot: 'bg-red-500',
    pill: 'bg-red-500/15 text-red-300 border-red-500/30',
  },
  caution: {
    label: 'Worth a look',
    text: 'text-amber-400',
    ring: 'ring-amber-500/70',
    dot: 'bg-amber-500',
    pill: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
  healthy: {
    label: 'Reporting normally',
    text: 'text-green-400',
    ring: 'ring-green-500/60',
    dot: 'bg-green-500',
    pill: 'bg-green-500/15 text-green-300 border-green-500/30',
  },
  unknown: {
    label: 'Not measured',
    text: 'text-[var(--text-tertiary)]',
    ring: 'ring-[var(--border-strong)]',
    dot: 'bg-slate-500',
    pill: 'bg-[var(--bg-overlay)] text-[var(--text-tertiary)] border-[var(--border-subtle)]',
  },
};

/** Fail to `unknown` for an unrecognised state — never to a colour that claims health. */
export function meta(state: string | null | undefined): StateMeta {
  return STATE_META[(state as HealthState)] ?? STATE_META.unknown;
}

/* The LABEL a stage shows, qualified by whether anything is outstanding with the source.
 *
 * Source faults deliberately do NOT change a stage's STATE (NK-129: they are faults in
 * what arrives, not a live signal, and folding eleven standing requests into the worst-of
 * would paint the map permanently red). But an adversarial review found the hole that
 * leaves: the promotion calendar can render the bare words "Reporting normally" while
 * carrying eight open requests — including the very fault this page exists to surface.
 *
 * So the state stays pure and the WORDS carry the qualification. A stage with nothing
 * outstanding reads "Reporting normally"; a stage with open requests never does.
 */
export function stateLabel(
  state: string | null | undefined,
  openSourceFaults: number = 0,
): string {
  const base = meta(state).label;
  if (openSourceFaults <= 0) return base;
  if (state === 'healthy') return `Reporting normally, waiting on the source`;
  return `${base} · ${openSourceFaults} with the source`;
}

export function stateIcon(state: string | null | undefined, size = 'w-3.5 h-3.5') {
  const s = (state as HealthState) ?? 'unknown';
  if (s === 'degraded') return <AlertCircle className={`${size} text-red-400`} />;
  if (s === 'caution') return <AlertTriangle className={`${size} text-amber-400`} />;
  if (s === 'healthy') return <CheckCircle className={`${size} text-green-400`} />;
  return <HelpCircle className={`${size} text-[var(--text-tertiary)]`} />;
}

/** The worst of several states. Absent/unrecognised states read as `unknown`. */
export function worstState(states: Array<string | null | undefined>): HealthState {
  let out: HealthState = 'unknown';
  for (const s of states) {
    const r = STATE_RANK[(s as HealthState)] ?? 0;
    if (r > STATE_RANK[out]) out = s as HealthState;
  }
  return out;
}

/** The feed verdict vocabulary, in planner words. Shared with the grid's Freshness cell. */
export const FEED_VERDICT_LABEL: Record<string, string> = {
  LIVE: 'Live',
  STALE: 'Behind',
  DEAD_LEGACY: 'History only',
  AWAITING_FEED: 'Waiting on the feed',
};
